/**
 * Module-level Web call media registry — survives React unmount for authoritative cleanup.
 * Phase 7B: startup cancellation, shutdown tombstone, late-resource blocking.
 */

import { shutdownAuditStep } from "./shutdownAudit";
import { stopWebRemoteAudioLevelMonitor } from "./webCallAudio";
import { resolveMediaStreamForVideoAttach } from "./webZegoVideoAttach";

const TOMBSTONE_DURATION_MS = 10000;
const STRICTMODE_REMOUNT_MS = 500;

/** @type {Map<string, WebCallMediaEntry>} */
const registryByCallId = new Map();

/** @type {Map<string, number>} */
const passiveUnmountAtByCallId = new Map();

let sessionCounter = 0;

const PASSIVE_SHUTDOWN_REASONS = new Set([
  "component_unmount_passive",
  "strictmode_unmount",
  "remount_probe",
  "native_placeholder_mount",
  "startRoom_effect_cleanup",
]);

function isActiveCallStatus(callStatus) {
  const status = String(callStatus || "").toLowerCase();
  return status === "ongoing" || status === "ringing" || status === "accepted";
}

export function isPassiveShutdownReason(reason) {
  return PASSIVE_SHUTDOWN_REASONS.has(String(reason || ""));
}

export function isRealShutdownReason(reason) {
  const r = String(reason || "").toLowerCase();
  if (isPassiveShutdownReason(reason)) return false;
  if (r === "component_unmount" || r === "startroom_effect_cleanup") return false;
  const realHints = [
    "web_hangup",
    "native_hangup",
    "remote_ended",
    "terminal_ended",
    "terminal_failed",
    "terminal_rejected",
    "callid_change",
    "manual_cleanup_after_end",
    "native_failure",
    "hangup",
    "report_block",
    "zegoerror",
    "shutdown:",
    "ended",
    "rejected",
    "cancelled",
    "failure",
    "native_call_ended",
  ];
  return realHints.some((hint) => r.includes(hint));
}

export function shouldCreateShutdownTombstone(reason, options = {}) {
  const { callStatus = "", exitTriggered = false, shutdownInProgress = false } = options;
  const r = String(reason || "");

  if (isPassiveShutdownReason(r) || r === "startRoom_effect_cleanup") {
    return false;
  }

  // Firebase still shows an active call — never create a tombstone without exit authorization.
  if (isActiveCallStatus(callStatus) && !exitTriggered && !shutdownInProgress) {
    console.log(
      `[webMediaRegistry] tombstone suppressed active call status=${callStatus} reason=${r}`
    );
    return false;
  }

  // StrictMode / loading-window unmount: status unknown, no exit signal — never tombstone.
  if (!String(callStatus || "").trim() && !exitTriggered && !shutdownInProgress) {
    if (
      r === "component_unmount" ||
      r.includes("component_unmount") ||
      r === "manual_cleanup_after_end" ||
      r.includes("duplicate_startup")
    ) {
      console.log(
        `[webMediaRegistry] tombstone suppressed unknown status passive reason=${r}`
      );
      return false;
    }
  }

  if (r === "component_unmount" || r.includes("component_unmount")) {
    if (exitTriggered || shutdownInProgress) return true;
    const status = String(callStatus || "").toLowerCase();
    if (status === "ongoing" || status === "ringing" || status === "accepted") {
      return false;
    }
    return isRealShutdownReason(r);
  }

  return isRealShutdownReason(r) || exitTriggered || shutdownInProgress;
}

export function noteCallRoomPassiveUnmount(callId) {
  const key = String(callId || "");
  if (!key) return;
  passiveUnmountAtByCallId.set(key, Date.now());
}

export function noteCallRoomRemount(callId) {
  const key = String(callId || "");
  if (!key) return false;
  const unmountAt = passiveUnmountAtByCallId.get(key);
  if (unmountAt && Date.now() - unmountAt < STRICTMODE_REMOUNT_MS) {
    console.log(
      `[webMediaRegistry] strictmode/passive remount detected, startup allowed callId=${key}`
    );
    passiveUnmountAtByCallId.delete(key);
    // Call doc may not be in ref yet during StrictMode remount — treat as active on /call/:id.
    tryClearPassiveTombstoneForActiveCall(key, "ongoing");
    return true;
  }
  return false;
}

export function tryClearPassiveTombstoneForActiveCall(callId, callStatus = null) {
  const key = String(callId || "");
  const entry = registryByCallId.get(key);
  if (!entry) return false;

  if (!isActiveCallStatus(callStatus)) return false;

  const blocked =
    isTombstoneActive(entry) ||
    entry.status === "cleaning" ||
    entry.status === "closed" ||
    entry.shutdownRequested;

  if (!blocked) return false;

  const previousReason = entry.shutdownReason || entry.status;
  const preserveSession = entry.sessionToken;

  entry.status = preserveSession ? "starting" : "starting";
  entry.shutdownRequested = false;
  entry.closedUntil = 0;
  entry.passiveTombstone = false;
  entry.shutdownReason = null;
  entry.isCleaning = false;

  console.log(
    `[webMediaRegistry] false tombstone cleared for active call callId=${key} previous=${previousReason}`
  );
  return true;
}

function createEntry(callId) {
  return {
    callId: String(callId),
    status: "starting",
    sessionToken: null,
    shutdownRequested: false,
    shutdownReason: null,
    passiveTombstone: false,
    closedUntil: 0,
    zegoEngine: null,
    roomId: null,
    localStream: null,
    localStreamID: null,
    remoteStreams: new Map(),
    remoteAudioElements: new Map(),
    remoteVideoElements: new Map(),
    localVideoElement: null,
    isCleaning: false,
  };
}

function ensureEntry(callId) {
  const key = String(callId || "");
  if (!key) return null;
  if (!registryByCallId.has(key)) {
    registryByCallId.set(key, createEntry(key));
  }
  return registryByCallId.get(key);
}

function isTombstoneActive(entry) {
  if (!entry) return false;
  if (entry.status === "closed" && entry.closedUntil > Date.now()) return true;
  return false;
}

function countTracks(stream) {
  const tracks = stream?.getTracks?.() || [];
  const audio = tracks.filter((t) => t.kind === "audio").length;
  const video = tracks.filter((t) => t.kind === "video").length;
  return { audio, video, total: tracks.length };
}

function findVideoElements(container) {
  if (!container) return [];
  if (container.tagName === "VIDEO") return [container];
  return Array.from(container.querySelectorAll("video"));
}

function findAudioElements(container) {
  if (!container) return [];
  if (container.tagName === "AUDIO") return [container];
  return Array.from(container.querySelectorAll("audio"));
}

function stopStreamTracks(stream) {
  if (!stream?.getTracks) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch (_) {}
  });
}

function clearElementSrcObject(element) {
  if (!element) return;
  try {
    element.pause?.();
    const stream = element.srcObject;
    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
    }
    element.srcObject = null;
  } catch (_) {}
}

function destroyEngineSafe(engine) {
  if (!engine?.destroyEngine) return;
  try {
    engine.destroyEngine();
  } catch (_) {}
}

function forceCleanStream(stream) {
  stopStreamTracks(stream);
}

function clearEntryMediaFields(entry) {
  if (!entry) return;
  stopStreamTracks(entry.localStream);
  for (const stream of entry.remoteStreams.values()) {
    stopStreamTracks(stream);
  }
  for (const audioEl of entry.remoteAudioElements.values()) {
    clearElementSrcObject(audioEl);
  }
  for (const videoEl of entry.remoteVideoElements.values()) {
    clearElementSrcObject(videoEl);
  }
  if (entry.localVideoElement) {
    clearElementSrcObject(entry.localVideoElement);
  }
  destroyEngineSafe(entry.zegoEngine);
  entry.zegoEngine = null;
  entry.localStream = null;
  entry.localStreamID = null;
  entry.roomId = null;
  entry.remoteStreams.clear();
  entry.remoteAudioElements.clear();
  entry.remoteVideoElements.clear();
  entry.localVideoElement = null;
}

function forceCleanupEntryResources(entry, reason) {
  if (!entry) return;
  console.log(`[webMediaRegistry] late resource force cleaned callId=${entry.callId}`, { reason });
  clearEntryMediaFields(entry);
}

function invalidateSession(entry) {
  if (!entry) return;
  entry.sessionToken = null;
}

function isLateRegistration(entry, sessionToken) {
  if (!entry) return true;
  if (entry.shutdownRequested) return true;
  if (entry.status === "cleaning" || entry.status === "closed") return true;
  if (isTombstoneActive(entry)) return true;
  if (sessionToken && entry.sessionToken && entry.sessionToken !== sessionToken) return true;
  return false;
}

function handleLateResource(callId, sessionToken, resourceType, resource) {
  const key = String(callId || "");
  console.error(
    `[webMediaRegistry] ERROR late resource registered after shutdown, force cleaned callId=${key}`,
    { resourceType, sessionToken }
  );
  if (resourceType === "engine") {
    destroyEngineSafe(resource);
  } else if (resourceType === "stream") {
    forceCleanStream(resource);
  } else if (resourceType === "element") {
    clearElementSrcObject(resource);
  } else if (resourceType === "container") {
    findVideoElements(resource).forEach(clearElementSrcObject);
    findAudioElements(resource).forEach(clearElementSrcObject);
  }
  console.log(`[webMediaRegistry] late resource force cleaned callId=${key}`);
}

function scheduleTombstonePurge(callId) {
  const key = String(callId || "");
  setTimeout(() => {
    const entry = registryByCallId.get(key);
    if (entry && entry.status === "closed" && Date.now() >= entry.closedUntil) {
      registryByCallId.delete(key);
      console.log(`[webMediaRegistry] tombstone expired, registry purged callId=${key}`);
    }
  }, TOMBSTONE_DURATION_MS + 250);
}

/**
 * Begin Web Zego startup for callId. Returns sessionToken or null if blocked by tombstone.
 */
export function beginWebCallStartup(callId, callStatus = null) {
  const key = String(callId || "");
  if (!key) return null;

  const isPassiveRemount = noteCallRoomRemount(key);
  if (tryClearPassiveTombstoneForActiveCall(key, callStatus)) {
    console.log(`[webZego] startup allowed after passive unmount callId=${key}`);
  }

  let entry = registryByCallId.get(key);

  // Last-resort: user still on an active call — never block startup behind a stale tombstone.
  if (isActiveCallStatus(callStatus) && entry && isTombstoneActive(entry)) {
    tryClearPassiveTombstoneForActiveCall(key, callStatus);
    entry = registryByCallId.get(key);
  }

  if (isTombstoneActive(entry) && !entry?.passiveTombstone) {
    console.log(
      `[webMediaRegistry] tombstone active callId=${key} reason=${entry?.shutdownReason || "unknown"}`
    );
    console.log(`[webMediaRegistry] startup blocked step=begin reason=tombstone callId=${key}`);
    return null;
  }

  if (
    isPassiveRemount &&
    entry &&
    entry.sessionToken &&
    (entry.status === "starting" || entry.status === "active") &&
    !entry.shutdownRequested
  ) {
    console.log(
      `[webMediaRegistry] startup allowed step=begin reason=remount_reuse callId=${key} session=${entry.sessionToken}`
    );
    console.log(
      `[webZego] strictmode remount reusing session callId=${key} session=${entry.sessionToken}`
    );
    return entry.sessionToken;
  }

  if (
    entry &&
    (entry.status === "starting" || entry.status === "active") &&
    entry.sessionToken
  ) {
    console.log(
      `[webMediaRegistry] duplicate startup detected, cancelling previous session callId=${key}`
    );
    forceCleanupEntryResources(entry, "duplicate_startup");
    invalidateSession(entry);
  }

  if (!entry || entry.status === "closed") {
    if (entry && isTombstoneActive(entry) && isActiveCallStatus(callStatus)) {
      tryClearPassiveTombstoneForActiveCall(key, callStatus);
      entry = registryByCallId.get(key);
    }
    if (!entry || entry.status === "closed") {
      entry = createEntry(key);
      registryByCallId.set(key, entry);
    }
  }

  const sessionToken = `${Date.now()}-${++sessionCounter}`;
  entry.status = "starting";
  entry.sessionToken = sessionToken;
  entry.shutdownRequested = false;
  entry.isCleaning = false;
  entry.closedUntil = 0;

  console.log(`[webMediaRegistry] startup allowed step=begin callId=${key} session=${sessionToken}`);
  console.log(`[webMediaRegistry] begin startup callId=${key} session=${sessionToken}`);
  return sessionToken;
}

export function getActiveWebCallSessionToken(callId) {
  return registryByCallId.get(String(callId || ""))?.sessionToken || null;
}

/**
 * Cancel a startup session only when sessionToken matches the active registry session.
 * Passive effect cleanups preserve the session for StrictMode remount reuse.
 */
export function cancelWebCallStartupSession(callId, sessionToken, reason = "unknown") {
  const key = String(callId || "");
  if (!key || !sessionToken) return false;

  const entry = registryByCallId.get(key);
  if (!entry) return false;

  const activeSession = entry.sessionToken || null;
  if (activeSession !== sessionToken) {
    console.log(
      `[webZego] ignored stale cleanup oldSession=${sessionToken} activeSession=${activeSession || "none"} reason=${reason}`
    );
    return false;
  }

  if (reason === "startRoom_effect_cleanup" || isPassiveShutdownReason(reason)) {
    console.log(
      `[webZego] passive effect cleanup session preserved for remount callId=${key} session=${sessionToken}`
    );
    return true;
  }

  entry.shutdownRequested = true;
  entry.shutdownReason = String(reason);
  invalidateSession(entry);
  console.log(
    `[webMediaRegistry] startup session cancelled callId=${key} session=${sessionToken} reason=${reason}`
  );
  return true;
}

export function markWebCallActive(callId, sessionToken) {
  const entry = registryByCallId.get(String(callId || ""));
  if (!entry || entry.sessionToken !== sessionToken) return;
  if (entry.shutdownRequested || entry.status === "cleaning" || entry.status === "closed") return;
  entry.status = "active";
}

export function markWebCallShutdownRequested(callId, reason = "unknown", options = {}) {
  const key = String(callId || "");
  if (!key) return false;

  if (!shouldCreateShutdownTombstone(reason, options)) {
    const existing = registryByCallId.get(key);
    if (
      existing &&
      isActiveCallStatus(options.callStatus) &&
      !options.exitTriggered &&
      !options.shutdownInProgress
    ) {
      tryClearPassiveTombstoneForActiveCall(key, options.callStatus);
    }
    if (
      String(reason).includes("component_unmount") ||
      reason === "startRoom_effect_cleanup"
    ) {
      console.log(`[webMediaRegistry] passive unmount no closed tombstone callId=${key}`);
    }
    return false;
  }

  let entry = registryByCallId.get(key);
  if (!entry) {
    entry = createEntry(key);
    registryByCallId.set(key, entry);
  }

  entry.shutdownRequested = true;
  entry.shutdownReason = String(reason);
  entry.passiveTombstone = false;
  if (entry.status !== "closed") {
    entry.status = "cleaning";
  }

  console.log(`[webMediaRegistry] shutdown requested callId=${key} reason=${reason}`);
  return true;
}

export function isWebCallStartupAllowed(callId, sessionToken, step = "") {
  const key = String(callId || "");
  const entry = registryByCallId.get(key);
  if (!entry) {
    if (step) {
      console.log(
        `[webMediaRegistry] startup blocked step=${step} reason=no_entry callId=${key}`
      );
    }
    return false;
  }
  if (!sessionToken || entry.sessionToken !== sessionToken) {
    if (step) {
      console.log(
        `[webMediaRegistry] startup blocked step=${step} reason=session_mismatch callId=${key} requested=${sessionToken || "none"} active=${entry.sessionToken || "none"}`
      );
      if (sessionToken && entry.sessionToken && sessionToken !== entry.sessionToken) {
        console.log(
          `[webZego] ignored stale async oldSession=${sessionToken} activeSession=${entry.sessionToken} step=${step}`
        );
      }
    }
    return false;
  }
  if (entry.shutdownRequested) {
    if (step) {
      console.log(
        `[webMediaRegistry] startup blocked step=${step} reason=shutdown_requested callId=${key}`
      );
    }
    return false;
  }
  if (entry.status === "cleaning" || entry.status === "closed") {
    if (isTombstoneActive(entry)) {
      console.log(`[webMediaRegistry] tombstone active callId=${key}`);
    }
    if (step) {
      console.log(
        `[webMediaRegistry] startup blocked step=${step} reason=call_closed callId=${key}`
      );
    }
    return false;
  }
  if (isTombstoneActive(entry)) {
    console.log(`[webMediaRegistry] tombstone active callId=${key}`);
    if (step) {
      console.log(
        `[webMediaRegistry] startup blocked step=${step} reason=tombstone callId=${key}`
      );
    }
    return false;
  }
  if (step) {
    console.log(`[webMediaRegistry] startup allowed step=${step} callId=${key}`);
  }
  return true;
}

export function cancelWebCallStartupIfBlocked(callId, sessionToken, step, resources = {}) {
  if (isWebCallStartupAllowed(callId, sessionToken, step)) return false;

  console.log(
    `[webMediaRegistry] startup cancelled after step=${step} callId=${String(callId || "")}`
  );

  const key = String(callId || "");
  const entry = registryByCallId.get(key);
  const { zegoEngine, localStream, remoteStream, container } = resources;
  if (localStream) forceCleanStream(localStream);
  if (remoteStream) forceCleanStream(remoteStream);
  if (container) {
    findVideoElements(container).forEach(clearElementSrcObject);
    findAudioElements(container).forEach(clearElementSrcObject);
  }
  destroyEngineSafe(zegoEngine);

  if (entry) {
    if (zegoEngine && entry.zegoEngine === zegoEngine) entry.zegoEngine = null;
    if (localStream && entry.localStream === localStream) entry.localStream = null;
    if (remoteStream) {
      for (const [streamID, stream] of entry.remoteStreams.entries()) {
        if (stream === remoteStream) entry.remoteStreams.delete(streamID);
      }
    }
  }

  console.log(`[webMediaRegistry] late resource force cleaned callId=${key}`);
  return true;
}

export function getWebCallMediaRegistry(callId) {
  return registryByCallId.get(String(callId || "")) || null;
}

export function hasWebCallMediaRegistry(callId) {
  const entry = getWebCallMediaRegistry(callId);
  if (!entry) return false;
  if (entry.status === "closed" && !entry.zegoEngine && !entry.localStream) {
    return false;
  }
  return !!(
    entry.zegoEngine ||
    entry.localStream ||
    entry.localStreamID ||
    entry.remoteStreams.size > 0 ||
    entry.remoteAudioElements.size > 0 ||
    entry.remoteVideoElements.size > 0 ||
    entry.localVideoElement
  );
}

export function registerWebCallMediaEngine(callId, zegoEngine, roomId = null, sessionToken = null) {
  const entry = ensureEntry(callId);
  if (!entry) return;

  if (isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "engine", zegoEngine);
    return;
  }

  entry.zegoEngine = zegoEngine || null;
  if (roomId) entry.roomId = String(roomId);
  console.log("[webMediaRegistry] register engine", {
    callId: entry.callId,
    roomId: entry.roomId,
    hasEngine: !!entry.zegoEngine,
    session: sessionToken,
  });
}

export function registerWebCallMediaLocalStream(
  callId,
  localStream,
  localStreamID = null,
  sessionToken = null
) {
  const entry = ensureEntry(callId);
  if (!entry) return;

  const videoTracks = localStream?.getVideoTracks?.() || [];
  const audioTracks = localStream?.getAudioTracks?.() || [];
  if (videoTracks.length > 0 || audioTracks.length > 0) {
    console.log(
      `[webCamera] local camera stream created audioTracks=${audioTracks.length} videoTracks=${videoTracks.length} callId=${entry.callId}`
    );
  }

  if (isLateRegistration(entry, sessionToken) || entry.shutdownRequested) {
    if (videoTracks.length > 0) {
      console.log(`[webCamera] BLOCKED camera creation after shutdown callId=${entry.callId}`);
    }
    handleLateResource(callId, sessionToken, "stream", localStream);
    return;
  }

  entry.localStream = localStream || null;
  if (localStreamID) entry.localStreamID = String(localStreamID);

  const previewStream =
    resolveMediaStreamForVideoAttach(localStream, "local").mediaStream || localStream;
  const counts = countTracks(previewStream);
  console.log(
    `[webMediaRegistry] register localStream tracks audio=${counts.audio} video=${counts.video}`,
    { callId: entry.callId, localStreamID: entry.localStreamID, session: sessionToken }
  );
}

export function registerWebCallMediaLocalVideoElement(callId, element, sessionToken = null) {
  const entry = ensureEntry(callId);
  if (!entry || !element) return;

  if (isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "element", element);
    return;
  }

  entry.localVideoElement = element;
  console.log("[webMediaRegistry] register videoElement", {
    callId: entry.callId,
    scope: "local",
    session: sessionToken,
  });
}

export function registerWebCallMediaRemoteStream(
  callId,
  streamID,
  remoteStream,
  sessionToken = null
) {
  const entry = ensureEntry(callId);
  if (!entry || !streamID) return;

  if (isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "stream", remoteStream);
    return;
  }

  entry.remoteStreams.set(String(streamID), remoteStream);
  const counts = countTracks(remoteStream);
  console.log(
    `[webMediaRegistry] register remoteStream streamID=${streamID} audio=${counts.audio} video=${counts.video}`,
    { callId: entry.callId, session: sessionToken }
  );
}

export function registerWebCallMediaRemoteVideoElement(
  callId,
  streamID,
  element,
  sessionToken = null
) {
  const entry = ensureEntry(callId);
  if (!entry || !streamID || !element) return;

  if (isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "element", element);
    return;
  }

  entry.remoteVideoElements.set(String(streamID), element);
  console.log("[webMediaRegistry] register videoElement", {
    callId: entry.callId,
    streamID,
    session: sessionToken,
  });
}

export function registerWebCallMediaRemoteAudioElement(
  callId,
  streamID,
  element,
  sessionToken = null
) {
  const entry = ensureEntry(callId);
  if (!entry || !streamID || !element) return;

  if (isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "element", element);
    return;
  }

  entry.remoteAudioElements.set(String(streamID), element);
  console.log("[webMediaRegistry] register audioElement", {
    callId: entry.callId,
    streamID,
    session: sessionToken,
  });
}

export function registerWebCallMediaElementsFromContainer(
  callId,
  streamID,
  container,
  scope = "remote",
  sessionToken = null
) {
  if (!container) return;

  const entry = ensureEntry(callId);
  if (entry && isLateRegistration(entry, sessionToken)) {
    handleLateResource(callId, sessionToken, "container", container);
    return;
  }

  const videos = findVideoElements(container);
  const audios = findAudioElements(container);
  videos.forEach((video) => {
    if (scope === "local") {
      registerWebCallMediaLocalVideoElement(callId, video, sessionToken);
    } else {
      registerWebCallMediaRemoteVideoElement(callId, streamID, video, sessionToken);
    }
  });
  audios.forEach((audio) => {
    registerWebCallMediaRemoteAudioElement(callId, streamID || "local", audio, sessionToken);
  });
}

function stopRegisteredTracks(stream, shutdownId, label) {
  if (!stream?.getTracks) return;
  stream.getTracks().forEach((track) => {
    const kind = track?.kind || "unknown";
    const before = track?.readyState || "unknown";
    try {
      track.stop();
      shutdownAuditStep(
        shutdownId,
        `stop_registered_${label}_track kind=${kind} readyState=${track?.readyState || before}`,
        true
      );
      console.log(
        `[shutdownAudit:web] stop registered ${label} track kind=${kind} readyState=${track?.readyState || before}`
      );
    } catch (e) {
      shutdownAuditStep(shutdownId, `stop_registered_${label}_track kind=${kind}`, false, {
        error: e?.message || e,
      });
    }
  });
}

function clearMediaElement(element, shutdownId, streamID, kind) {
  if (!element) return;
  try {
    element.pause?.();
    const stream = element.srcObject;
    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (_) {}
      });
    }
    element.srcObject = null;
    shutdownAuditStep(shutdownId, `clear_registered_${kind}_element streamID=${streamID}`, true);
    console.log(`[shutdownAudit:web] clear registered ${kind} element streamID=${streamID}`);
  } catch (e) {
    shutdownAuditStep(shutdownId, `clear_registered_${kind}_element streamID=${streamID}`, false, {
      error: e?.message || e,
    });
  }
}

export function logRemainingRegistryResources(callId, shutdownId) {
  const entry = registryByCallId.get(String(callId || ""));
  if (!entry) {
    return;
  }

  if (entry.status === "closed" && !entry.zegoEngine && !entry.localStream) {
    return;
  }

  const localTracks = entry.localStream?.getTracks?.() || [];
  localTracks.forEach((track) => {
    if (track.readyState !== "ended") {
      console.error(
        `[shutdownAudit:web] ERROR remaining local track kind=${track.kind} readyState=${track.readyState}`,
        { shutdownId, callId }
      );
    }
  });

  for (const [streamID, stream] of entry.remoteStreams.entries()) {
    (stream?.getTracks?.() || []).forEach((track) => {
      if (track.readyState !== "ended") {
        console.error(
          `[shutdownAudit:web] ERROR remaining remote track streamID=${streamID} kind=${track.kind} readyState=${track.readyState}`,
          { shutdownId, callId }
        );
      }
    });
  }

  for (const [streamID, audioEl] of entry.remoteAudioElements.entries()) {
    if (audioEl.srcObject || !audioEl.paused) {
      console.error(
        `[shutdownAudit:web] ERROR remaining audio element streamID=${streamID} paused=${audioEl.paused} hasSrcObject=${!!audioEl.srcObject}`,
        { shutdownId, callId }
      );
    }
  }

  for (const [streamID, videoEl] of entry.remoteVideoElements.entries()) {
    if (videoEl.srcObject) {
      console.error(
        `[shutdownAudit:web] ERROR remaining video element streamID=${streamID} hasSrcObject=true`,
        { shutdownId, callId }
      );
    }
  }

  if (entry.localVideoElement?.srcObject) {
    console.error("[shutdownAudit:web] ERROR remaining local video element hasSrcObject=true", {
      shutdownId,
      callId,
    });
  }

  if (entry.zegoEngine) {
    console.error("[shutdownAudit:web] ERROR zego engine still not null", { shutdownId, callId });
  }
}

export function getRegistryCleanupState(callId) {
  const entry = registryByCallId.get(String(callId || ""));
  if (!entry) {
    return {
      localVideoTracksEnded: true,
      localAudioTracksEnded: true,
      remoteElementsCleared: true,
      remoteTracksEnded: true,
      zegoEngineNull: true,
      registryPresent: false,
    };
  }

  const tombstoneOnly =
    entry.status === "closed" &&
    !entry.zegoEngine &&
    !entry.localStream &&
    entry.remoteStreams.size === 0;

  if (tombstoneOnly) {
    return {
      localVideoTracksEnded: true,
      localAudioTracksEnded: true,
      remoteElementsCleared: true,
      remoteTracksEnded: true,
      zegoEngineNull: true,
      registryPresent: true,
      isCleaning: false,
      status: entry.status,
      tombstoneActive: isTombstoneActive(entry),
    };
  }

  const localTracks = entry.localStream?.getTracks?.() || [];
  const localVideoEnded =
    localTracks.filter((t) => t.kind === "video").every((t) => t.readyState === "ended");
  const localAudioEnded =
    localTracks.filter((t) => t.kind === "audio").every((t) => t.readyState === "ended");

  let remoteTracks = [];
  for (const stream of entry.remoteStreams.values()) {
    remoteTracks = remoteTracks.concat(stream?.getTracks?.() || []);
  }

  const remoteTracksEnded =
    remoteTracks.length === 0 || remoteTracks.every((t) => t.readyState === "ended");

  const remoteElementsCleared =
    !entry.localVideoElement?.srcObject &&
    Array.from(entry.remoteVideoElements.values()).every((el) => !el?.srcObject) &&
    Array.from(entry.remoteAudioElements.values()).every((el) => !el?.srcObject && el?.paused !== false);

  return {
    localVideoTracksEnded: localTracks.length === 0 || localVideoEnded,
    localAudioTracksEnded: localTracks.length === 0 || localAudioEnded,
    remoteElementsCleared,
    remoteTracksEnded,
    zegoEngineNull: !entry.zegoEngine,
    registryPresent: true,
    isCleaning: entry.isCleaning,
    status: entry.status,
    tombstoneActive: isTombstoneActive(entry),
  };
}

/**
 * Authoritative Web media cleanup using module-level registry (not React refs).
 */
export async function cleanupWebCallMediaRegistry(callId, shutdownId, shutdownReason = "unknown", options = {}) {
  const key = String(callId || "");
  const entry = registryByCallId.get(key);
  if (!entry) {
    console.log(`[shutdownAudit:web] registry cleanup skipped no entry callId=${key}`);
    return { cleaned: false, reason: "no_registry" };
  }

  if (!shouldCreateShutdownTombstone(shutdownReason, options)) {
    console.log(`[shutdownAudit:web] component_unmount passive skip tombstone callId=${key}`);
    console.log(`[webMediaRegistry] passive unmount no closed tombstone callId=${key}`);
    entry.isCleaning = false;
    entry.shutdownRequested = false;
    return { cleaned: false, reason: "passive_skip", passive: true };
  }

  if (
    isActiveCallStatus(options.callStatus) &&
    !options.exitTriggered &&
    !options.shutdownInProgress
  ) {
    console.log(
      `[webMediaRegistry] registry cleanup aborted — would tombstone active call callId=${key} reason=${shutdownReason}`
    );
    entry.isCleaning = false;
    entry.shutdownRequested = false;
    return { cleaned: false, reason: "active_call_protected" };
  }

  if (entry.isCleaning && entry.shutdownRequested) {
    // Allow re-entry when shutdown was marked but full cleanup not finished
  } else if (entry.isCleaning) {
    return { cleaned: false, reason: "already_cleaning" };
  }

  entry.isCleaning = true;
  entry.shutdownRequested = true;
  entry.shutdownReason = String(shutdownReason);
  entry.passiveTombstone = false;
  if (entry.status !== "closed") {
    entry.status = "cleaning";
  }
  console.log(`[shutdownAudit:web] registry cleanup start callId=${key} shutdownId=${shutdownId}`);

  const zg = entry.zegoEngine;

  for (const streamID of Array.from(entry.remoteStreams.keys())) {
    stopWebRemoteAudioLevelMonitor(streamID);
    if (zg?.stopPlayingStream) {
      try {
        zg.stopPlayingStream(streamID);
        console.log(`[shutdownAudit:web] stopPlayingStream done streamID=${streamID}`);
        shutdownAuditStep(shutdownId, "registry_stopPlayingStream", true, { streamID });
      } catch (e) {
        shutdownAuditStep(shutdownId, "registry_stopPlayingStream", false, {
          streamID,
          error: e?.message || e,
        });
      }
    }
  }

  if (zg?.stopPublishingStream && entry.localStreamID) {
    try {
      zg.stopPublishingStream(entry.localStreamID);
      console.log(`[shutdownAudit:web] stopPublishingStream done streamID=${entry.localStreamID}`);
      shutdownAuditStep(shutdownId, "registry_stopPublishingStream", true, {
        streamID: entry.localStreamID,
      });
    } catch (e) {
      shutdownAuditStep(shutdownId, "registry_stopPublishingStream", false, {
        streamID: entry.localStreamID,
        error: e?.message || e,
      });
    }
  }

  if (zg?.logoutRoom && entry.roomId) {
    try {
      await zg.logoutRoom(entry.roomId);
      shutdownAuditStep(shutdownId, "registry_leaveRoom", true, { roomId: entry.roomId });
      console.log("[shutdownAudit:web] leaveRoom done");
    } catch (e) {
      shutdownAuditStep(shutdownId, "registry_leaveRoom", false, { error: e?.message || e });
    }
  }

  stopRegisteredTracks(entry.localStream, shutdownId, "local");
  for (const [streamID, stream] of entry.remoteStreams.entries()) {
    stopRegisteredTracks(stream, shutdownId, `remote streamID=${streamID}`);
  }

  for (const [streamID, audioEl] of entry.remoteAudioElements.entries()) {
    try {
      audioEl.pause?.();
      clearMediaElement(audioEl, shutdownId, streamID, "audio");
      if (audioEl.parentNode) {
        audioEl.parentNode.removeChild(audioEl);
      }
    } catch (e) {
      shutdownAuditStep(shutdownId, "remove_audio_element", false, {
        streamID,
        error: e?.message || e,
      });
    }
  }

  for (const [streamID, videoEl] of entry.remoteVideoElements.entries()) {
    clearMediaElement(videoEl, shutdownId, streamID, "video");
  }
  if (entry.localVideoElement) {
    clearMediaElement(entry.localVideoElement, shutdownId, "local", "video");
  }

  if (zg?.destroyEngine) {
    try {
      zg.destroyEngine();
      entry.zegoEngine = null;
      console.log("[shutdownAudit:web] destroyEngine done");
      shutdownAuditStep(shutdownId, "registry_destroyEngine", true);
    } catch (e) {
      shutdownAuditStep(shutdownId, "registry_destroyEngine", false, { error: e?.message || e });
    }
  } else {
    entry.zegoEngine = null;
  }

  entry.localStream = null;
  entry.localStreamID = null;
  entry.roomId = null;
  entry.remoteStreams.clear();
  entry.remoteAudioElements.clear();
  entry.remoteVideoElements.clear();
  entry.localVideoElement = null;
  entry.sessionToken = null;
  entry.status = "closed";
  entry.closedUntil = Date.now() + TOMBSTONE_DURATION_MS;
  entry.isCleaning = false;
  entry.passiveTombstone = false;

  if (
    isActiveCallStatus(options.callStatus) &&
    !options.exitTriggered &&
    !options.shutdownInProgress
  ) {
    console.log(
      `[webMediaRegistry] tombstone roll-back active call still live callId=${key}`
    );
    entry.status = "starting";
    entry.closedUntil = 0;
    entry.shutdownRequested = false;
    entry.shutdownReason = null;
    return { cleaned: false, reason: "active_call_protected_late" };
  }

  console.log(
    `[webMediaRegistry] closed tombstone active callId=${key} reason=${entry.shutdownReason || shutdownReason}`
  );
  console.log(`[shutdownAudit:web] registry tombstone set callId=${key}`);
  shutdownAuditStep(shutdownId, "registry_tombstone", true, {
    callId: key,
    closedUntil: entry.closedUntil,
    reason: entry.shutdownReason || shutdownReason,
  });

  scheduleTombstonePurge(key);

  return { cleaned: true };
}

export function clearWebCallMediaRegistry(callId) {
  const key = String(callId || "");
  const entry = registryByCallId.get(key);
  if (!entry) return;
  if (isTombstoneActive(entry)) {
    console.log(`[webMediaRegistry] clearWebCallMediaRegistry skipped tombstone active callId=${key}`);
    return;
  }
  registryByCallId.delete(key);
}
